import { useState } from "react";
import { Button } from "react-bootstrap";
import * as XLSX from "xlsx";
import "../../../../styles/BulkImport.css";
import { useAddBulkImportMutation } from "api/ruleEngineApi";
import {
  showToastError,
  showToastSuccess,
} from "components/toastify/ToastNotifications";
import { BulkImportError } from "model/rule-engine-model";

// import ghg from "../../../../../public/bulk_import_templates/"

interface BulkImportProps {
  onClose?: () => void;
  onSuccess?: () => void;
}

interface Rule {
  businessLine: string;
  sellCurrency: string;
  costCurrency: string;
  sellRate: number;
  costAmount: number;
  externalSupplier: string;
  externalSupplierCost: number;
  effectiveDate: string;
}

interface BulkImportPayload {
  rules: Rule[];
}

interface CSVRow {
  [key: string]: string;
}

const BulkImport: React.FC<BulkImportProps> = ({ onClose, onSuccess }) => {
  const [createBulkImport, { isLoading: isCreatingBulkImport }] =
    useAddBulkImportMutation();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<Rule[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [showTemplateDropdown, setShowTemplateDropdown] =
    useState<boolean>(false);

  // List of columns to ignore during processing
  const IGNORED_COLUMNS = [
    "s/n",
    "sn",
    "serial",
    "serialnumber",
    "serial_number",
    "serialNumber",
    "number",
    "no",
    "index",
    "row",
    "rowno",
    "row_no",
    "rowNumber",
  ];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setParsedData(null);
      setParseError(null);
    }
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      setParsedData(null);
      setParseError(null);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  // Function to check if a column should be ignored
  const shouldIgnoreColumn = (columnName: string): boolean => {
    const normalizedColumnName = columnName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
    return IGNORED_COLUMNS.some(
      (ignored) => normalizedColumnName === ignored.replace(/[^a-z0-9]/g, "")
    );
  };

  // Function to parse CSV content with improved parsing
  const parseCSV = (csvContent: string): CSVRow[] => {
    const lines = csvContent
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line !== "");

    if (lines.length < 2) {
      throw new Error(
        "CSV must contain at least a header row and one data row"
      );
    }

    // Parse CSV line handling quotes properly
    const parseCSVLine = (line: string): string[] => {
      const result: string[] = [];
      let current = "";
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];

        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            // Handle escaped quotes
            current += '"';
            i++; // Skip next quote
          } else {
            // Toggle quote state
            inQuotes = !inQuotes;
          }
        } else if (char === "," && !inQuotes) {
          // End of field
          result.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }

      // Add the last field
      result.push(current.trim());
      return result;
    };

    const rawHeaders = parseCSVLine(lines[0]);

    // Filter out ignored columns and normalize remaining headers
    const headerMapping: { [key: string]: string } = {};
    const filteredHeaders: string[] = [];

    rawHeaders.forEach((header, index) => {
      const cleanHeader = header.toLowerCase().replace(/['"]/g, "").trim();
      if (!shouldIgnoreColumn(cleanHeader)) {
        filteredHeaders.push(cleanHeader);
        headerMapping[index.toString()] = cleanHeader;
      } else {
        
      }
    });

    // 
    // 

    const rows: CSVRow[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      const values = parseCSVLine(line);
      const row: CSVRow = {};

      // Only process non-ignored columns
      Object.entries(headerMapping).forEach(([originalIndex, headerName]) => {
        const index = parseInt(originalIndex);
        row[headerName] = values[index]
          ? values[index].replace(/['"]/g, "").trim()
          : "";
      });

      
      rows.push(row);
    }

    return rows;
  };

  // Function to convert CSV row to Rule object with type checking
  const convertToRule = (row: CSVRow, rowIndex: number): Rule => {
    const errors: string[] = [];

    // 
    // 
    // 

    // Map headers to expected field names (handle both formats)
    const getFieldValue = (fieldNames: string[]): string => {
      for (const fieldName of fieldNames) {
        if (row[fieldName] !== undefined && row[fieldName] !== "") {
          
          return row[fieldName];
        }
      }
      // 
      return "";
    };

    // Extract and validate required fields with flexible header matching
    const businessLine = getFieldValue([
      "businessline",
      "business_line",
      "businessLine",
    ]);
    const sellCurrency = getFieldValue([
      "sellcurrency",
      "sell_currency",
      "sellCurrency",
    ]);
    const costCurrency = getFieldValue([
      "costcurrency",
      "cost_currency",
      "costCurrency",
    ]);
    const externalSupplier = getFieldValue([
      "externalsupplier",
      "external_supplier",
      "externalSupplier",
    ]);

    // Parse numeric fields with validation
    const sellRateStr =
      getFieldValue(["sellrate", "sell_rate", "sellRate"]) || "0";
    const costAmountStr =
      getFieldValue(["costamount", "cost_amount", "costAmount"]) || "0";
    const externalSupplierCostStr =
      getFieldValue([
        "externalsuppliercost",
        "external_supplier_cost",
        "externalSupplierCost",
      ]) || "0";

    // 
    // 
    // 
    // 
    // 
    // 
    // 
    // 

    const sellRate = parseFloat(sellRateStr);
    const costAmount = parseFloat(costAmountStr);
    const externalSupplierCost = parseFloat(externalSupplierCostStr);

    // Validate numeric fields
    if (isNaN(sellRate)) {
      errors.push(`Invalid sellRate value: ${sellRateStr}`);
    }
    if (isNaN(costAmount)) {
      errors.push(`Invalid costAmount value: ${costAmountStr}`);
    }
    if (isNaN(externalSupplierCost)) {
      errors.push(
        `Invalid externalSupplierCost value: ${externalSupplierCostStr}`
      );
    }

    // Handle effective date
    let effectiveDate = getFieldValue([
      "effectivedate",
      "effective_date",
      "effectiveDate",
    ]);
    if (effectiveDate === "") {
      effectiveDate = new Date().toISOString();
      // 
    } else {
      // Try to parse the date and convert to ISO format
      const parsedDate = new Date(effectiveDate);
      if (isNaN(parsedDate.getTime())) {
        errors.push(`Invalid effectiveDate value: ${effectiveDate}`);
        effectiveDate = new Date().toISOString();
      } else {
        effectiveDate = parsedDate.toISOString();
      }
      // 
    }

    // Validate required string fields
    if (!businessLine.trim()) {
      errors.push("businessLine is required");
      showToastError("❌ businessLine validation failed - field is empty");
    } else {
      
    }

    if (!sellCurrency.trim()) {
      errors.push("sellCurrency is required");
      showToastError("❌ sellCurrency validation failed - field is empty");
    } else {
      
    }

    if (!costCurrency.trim()) {
      errors.push("costCurrency is required");
      showToastError("❌ costCurrency validation failed - field is empty");
    } else {
      
    }

    if (!externalSupplier.trim()) {
      errors.push("externalSupplier is required");
      
    } else {
      
    }

    if (errors.length > 0) {
      
      throw new Error(`Row ${rowIndex + 1}: ${errors.join(", ")}`);
    }

    // 

    return {
      businessLine: businessLine.trim(),
      sellCurrency: sellCurrency.trim(),
      costCurrency: costCurrency.trim(),
      sellRate,
      costAmount,
      externalSupplier: externalSupplier.trim(),
      externalSupplierCost,
      effectiveDate,
    };
  };

  // Function to parse Excel file
  const parseExcelFile = (file: File): Promise<CSVRow[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          if (!data) {
            reject(new Error("Failed to read file"));
            return;
          }

          // Parse the Excel file
          const workbook = XLSX.read(data, { type: "binary" });

          // Get the first worksheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // 
          // 

          // Convert worksheet to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          // 

          if (!Array.isArray(jsonData) || jsonData.length < 2) {
            reject(
              new Error(
                "Excel file must contain at least a header row and one data row"
              )
            );
            return;
          }

          // Process headers and filter out ignored columns
          const rawHeaders = jsonData[0] as any[];
          const headerMapping: { [key: string]: string } = {};
          const filteredHeaders: string[] = [];

          rawHeaders.forEach((header, index) => {
            const cleanHeader = String(header || "")
              .toLowerCase()
              .trim();
            if (!shouldIgnoreColumn(cleanHeader)) {
              filteredHeaders.push(cleanHeader);
              headerMapping[index.toString()] = cleanHeader;
            } else {
              
            }
          });

          // 
          // 

          const rows: CSVRow[] = [];

          for (let i = 1; i < jsonData.length; i++) {
            const rowData = jsonData[i] as any[];
            const row: CSVRow = {};

            // Only process non-ignored columns
            Object.entries(headerMapping).forEach(
              ([originalIndex, headerName]) => {
                const index = parseInt(originalIndex);
                const value = rowData[index];
                row[headerName] =
                  value !== undefined && value !== null
                    ? String(value).trim()
                    : "";
              }
            );

            // 
            rows.push(row);
          }

          resolve(rows);
        } catch (error) {
          console.error("Error parsing Excel file:", error);
          reject(
            new Error(
              `Failed to parse Excel file: ${
                error instanceof Error ? error.message : "Unknown error"
              }`
            )
          );
        }
      };

      reader.onerror = () => reject(new Error("Error reading Excel file"));
      reader.readAsBinaryString(file);
    });
  };

  // Function to analyze file structure
  const analyzeFileStructure = async (file: File): Promise<void> => {
    // 
    // 
    // 
    // 

    const isExcel =
      file.name.toLowerCase().endsWith(".xlsx") ||
      file.name.toLowerCase().endsWith(".xls") ||
      file.type.includes("spreadsheet");

    // 

    try {
      let rows: CSVRow[];

      if (isExcel) {
        rows = await parseExcelFile(file);
      } else {
        // Handle CSV files
        const fileContent = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result;
            if (typeof result === "string") {
              resolve(result);
            } else {
              reject(new Error("Failed to read file"));
            }
          };
          reader.onerror = () => reject(new Error("Error reading file"));
          reader.readAsText(file);
        });
        rows = parseCSV(fileContent);
      }

      // Show first 4 data rows
      const previewRows = rows.slice(0, 4);
      // 

      // previewRows.forEach((row, index) => {
      //   
      //   
      // });

      // Check for required fields
      if (rows.length > 0) {
        const firstRow = rows[0];
        const availableHeaders = Object.keys(firstRow);

        // 
        // 

        const requiredFields = [
          "businessline",
          "sellcurrency",
          "costcurrency",
          "externalsupplier",
        ];
        const fieldVariations = {
          businessline: ["businessline", "business_line", "businessLine"],
          sellcurrency: ["sellcurrency", "sell_currency", "sellCurrency"],
          costcurrency: ["costcurrency", "cost_currency", "costCurrency"],
          externalsupplier: [
            "externalsupplier",
            "external_supplier",
            "externalSupplier",
          ],
        };

        requiredFields.forEach((field) => {
          const found = fieldVariations[
            field as keyof typeof fieldVariations
          ].some((variation) => availableHeaders.includes(variation));
          // 
          if (found) {
            const matchedHeader = fieldVariations[
              field as keyof typeof fieldVariations
            ].find((variation) => availableHeaders.includes(variation));
            
          }
        });
      }
    } catch (error) {
      console.error("Error analyzing file:", error);
      throw error;
    }
  };

  // Function to handle template downloads
  const handleTemplateDownload = (format: "csv" | "xlsx") => {
    try {
      // Create a link element for download
      const link = document.createElement("a");

      if (format === "csv") {
        link.href = "/bulk_import_templates/bulk_import_template.csv";
        link.download = "bulk_import_template.csv";
      } else {
        link.href = "/bulk_import_templates/bulk_import_template.xlsx";
        link.download = "bulk_import_template.xlsx";
      }

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Hide dropdown after download
      setShowTemplateDropdown(false);

      showToastSuccess(
        `${format.toUpperCase()} template downloaded successfully`
      );
    } catch (error) {
      console.error("Error downloading template:", error);
      showToastError(`Failed to download ${format.toUpperCase()} template`);
    }
  };

  const displayBulkImportErrors = (
    errors: BulkImportError[],
    totalFailed: number
  ) => {
    const errorMessages = errors
      .slice(0, 3)
      .map((error) => `Row ${error.rowNumber}: ${error.error}`)
      .join("\n");

    const additionalErrors =
      errors.length > 3 ? `\n...and ${errors.length - 3} more errors` : "";

    showToastError(
      `Bulk import completed with ${totalFailed} failed imports:\n${errorMessages}${additionalErrors}`
    );
  };

  // Function to process the uploaded file
  const processFile = async (): Promise<void> => {
    if (!selectedFile) {
      setParseError("No file selected");
      return;
    }

    setIsProcessing(true);
    setParseError(null);

    try {
      // Analyze the file structure first
      await analyzeFileStructure(selectedFile);

      const isExcel =
        selectedFile.name.toLowerCase().endsWith(".xlsx") ||
        selectedFile.name.toLowerCase().endsWith(".xls") ||
        selectedFile.type.includes("spreadsheet");

      let csvRows: CSVRow[];

      if (isExcel) {
        csvRows = await parseExcelFile(selectedFile);
      } else {
        const fileContent = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result;
            if (typeof result === "string") {
              resolve(result);
            } else {
              reject(new Error("Failed to read file"));
            }
          };
          reader.onerror = () => reject(new Error("Error reading file"));
          reader.readAsText(selectedFile);
        });
        csvRows = parseCSV(fileContent);
      }

      // Process ALL rows
      // 
      const rules: Rule[] = csvRows.map((row, index) =>
        convertToRule(row, index)
      );

      setParsedData(rules);
      setParseError(null);
      // 
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      setParseError(`Error processing file: ${errorMessage}`);
      setParsedData(null);
      console.error("Processing failed:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Function to submit the bulk import - Fixed typing issues
  const handleSubmit = async (): Promise<void> => {
    if (!parsedData || parsedData.length === 0) {
      setParseError("No valid data to submit");
      return;
    }

    try {
      const payload: BulkImportPayload = {
        rules: parsedData,
      };

      // 

      // Fixed: Properly type the response
      const response = await createBulkImport(payload).unwrap();

      // Fixed: Added proper type checking
      if (
        response.data &&
        response.data.failedImports > 0 &&
        response.data.errors &&
        response.data.errors.length > 0
      ) {
        // 
        displayBulkImportErrors(
          response.data.errors,
          response.data.failedImports
        );
        setParseError(
          `Failed to import ${response.data.failedImports} records. See details above.`
        );
        return;
      }

      // Reset form on success
      setSelectedFile(null);
      setParsedData(null);
      setParseError(null);

      showToastSuccess("Bulk import submitted successfully");

      if (onSuccess) {
        onSuccess();
      }
      if (onClose) {
        onClose();
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to submit bulk import";
      setParseError(`Submission error: ${errorMessage}`);
    }
  };

  const UploadIcon: React.FC = () => (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#9ca3af"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
      <polyline points="14,2 14,8 20,8"></polyline>
      <path d="M12 18v-6"></path>
      <path d="m9 15 3-3 3 3"></path>
    </svg>
  );

  return (
    <div className="bulk-import-container">
      {/* CSV Upload Section */}
      <div className="csv-upload-section">
        <div className="csv-template-header">
          <h2 className="csv-template-title">File Upload</h2>
          <p className="csv-template-description">
            Upload your Excel (.xlsx, .xls) or CSV file with currency rules
          </p>
        </div>

        <div
          className="upload-area"
          onDrop={handleFileDrop}
          onDragOver={handleDragOver}
        >
          <div className="upload-content">
            <UploadIcon />
            <h3 className="upload-title">Upload Excel or CSV File</h3>
            <p className="upload-description">
              Drag and drop your .xlsx, .xls, or .csv file here, or click to
              browse
            </p>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="file-input"
              id="csv-file-input"
            />
            <label htmlFor="csv-file-input" className="choose-file-btn">
              Choose File
            </label>
            {selectedFile && (
              <p className="selected-file">Selected: {selectedFile.name}</p>
            )}
          </div>
        </div>

        {/* Error Display */}
        {parseError && (
          <div
            className="error-message"
            style={{ color: "red", margin: "10px 0" }}
          >
            {parseError}
          </div>
        )}

        {/* Success Display */}
        {parsedData && (
          <div
            className="success-message"
            style={{ color: "green", margin: "10px 0" }}
          >
            Successfully parsed {parsedData.length} rules from CSV
          </div>
        )}

        <div className="upload-actions">
          <div
            className="template-dropdown-container"
            style={{ position: "relative", display: "inline-block" }}
          >
            <Button
              variant="outline-secondary"
              className="download-template-btn"
              onMouseEnter={() => setShowTemplateDropdown(true)}
              onMouseLeave={() => setShowTemplateDropdown(false)}
            >
              Download Template
            </Button>

            {showTemplateDropdown && (
              <div
                className="template-dropdown"
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  backgroundColor: "white",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  zIndex: 1000,
                  minWidth: "200px",
                }}
                onMouseEnter={() => setShowTemplateDropdown(true)}
                onMouseLeave={() => setShowTemplateDropdown(false)}
              >
                <div
                  className="dropdown-item"
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    borderBottom: "1px solid #eee",
                    fontSize: "14px",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f5f5f5")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "white")
                  }
                  onClick={() => handleTemplateDownload("csv")}
                >
                  📄 Download .csv template
                </div>
                <div
                  className="dropdown-item"
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f5f5f5")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "white")
                  }
                  onClick={() => handleTemplateDownload("xlsx")}
                >
                  📊 Download .xlsx template
                </div>
              </div>
            )}
          </div>

          <Button
            variant="primary"
            className="process-upload-btn"
            onClick={processFile}
            disabled={!selectedFile || isProcessing}
          >
            {isProcessing ? "Processing..." : "Process Upload"}
          </Button>
          {parsedData && (
            <Button
              variant="success"
              onClick={handleSubmit}
              disabled={isCreatingBulkImport}
              style={{ marginLeft: "10px" }}
            >
              {isCreatingBulkImport ? "Submitting..." : "Submit Bulk Import"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImport;
